// Local Storage Service - Guest Mode
// Solo manejo de tickets, puntos y persistencia local
// NO contiene lógica de premios ni probabilidades

const STORAGE_KEY = 'ruleta_fintech_user';

// Meta de puntos por defecto
const DEFAULT_POINTS_GOAL = 1000;

// Default guest user data
const getDefaultUserData = () => ({
  points: 0,
  pointsGoal: DEFAULT_POINTS_GOAL,
  normalTickets: 3,
  goldenTickets: 0,
  normalTicketsExpiry: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  lastSpinTime: null,
  totalSpins: 0,
  rewards: [],
  spinHistory: [],
  purchases: [],
  createdAt: new Date().toISOString()
});

// Get user data from localStorage
export const getUserData = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      if (data.normalTicketsExpiry) {
        const expiry = new Date(data.normalTicketsExpiry);
        if (new Date() > expiry) {
          data.normalTickets = 0;
          data.normalTicketsExpiry = null;
          saveUserData(data);
        }
      }
      return data;
    }
    const newUser = getDefaultUserData();
    saveUserData(newUser);
    return newUser;
  } catch (error) {
    console.error('Error reading user data:', error);
    return getDefaultUserData();
  }
};

// Save user data to localStorage
export const saveUserData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving user data:', error);
    return false;
  }
};

// Obtener datos de puntos
export const getPointsData = () => {
  const data = getUserData();
  return {
    points: data.points || 0,
    pointsGoal: data.pointsGoal || DEFAULT_POINTS_GOAL,
    progress: Math.min((data.points || 0) / (data.pointsGoal || DEFAULT_POINTS_GOAL), 1),
    isReady: (data.points || 0) >= (data.pointsGoal || DEFAULT_POINTS_GOAL)
  };
};

// Tickets
export const addTickets = (normalTickets, goldenTickets) => {
  const data = getUserData();
  
  if (normalTickets > 0) {
    data.normalTickets = (data.normalTickets || 0) + normalTickets;
    data.normalTicketsExpiry = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  }
  
  if (goldenTickets > 0) {
    data.goldenTickets = (data.goldenTickets || 0) + goldenTickets;
  }
  
  saveUserData(data);
  return data;
};

export const useTicket = (preferGolden = false) => {
  const data = getUserData();
  
  let normalAvailable = data.normalTickets || 0;
  if (data.normalTicketsExpiry) {
    const expiry = new Date(data.normalTicketsExpiry);
    if (new Date() > expiry) {
      normalAvailable = 0;
      data.normalTickets = 0;
      data.normalTicketsExpiry = null;
    }
  }
  
  const goldenAvailable = data.goldenTickets || 0;
  
  if (preferGolden && goldenAvailable > 0) {
    data.goldenTickets--;
    saveUserData(data);
    return { success: true, ticketType: 'golden' };
  }
  
  if (normalAvailable > 0) {
    data.normalTickets--;
    saveUserData(data);
    return { success: true, ticketType: 'normal' };
  }
  
  if (goldenAvailable > 0) {
    data.goldenTickets--;
    saveUserData(data);
    return { success: true, ticketType: 'golden' };
  }
  
  return { success: false, error: 'No tienes tiquetes disponibles' };
};

export const canSpin = () => {
  const data = getUserData();
  
  let normalAvailable = data.normalTickets || 0;
  if (data.normalTicketsExpiry) {
    const expiry = new Date(data.normalTicketsExpiry);
    if (new Date() > expiry) {
      normalAvailable = 0;
    }
  }
  
  const goldenAvailable = data.goldenTickets || 0;
  const total = normalAvailable + goldenAvailable;
  
  return {
    canSpin: total > 0,
    normalTickets: normalAvailable,
    goldenTickets: goldenAvailable,
    needsTickets: total === 0
  };
};

// Guardar resultado del spin (viene del backend)
export const recordSpinResult = (backendResult, ticketType) => {
  const data = getUserData();
  
  const spinRecord = {
    id: backendResult.spinId || Date.now().toString(),
    prize: backendResult.prize,
    pointsAwarded: backendResult.pointsAwarded || 0,
    ticketType: ticketType,
    timestamp: new Date().toISOString()
  };
  
  data.spinHistory = [spinRecord, ...(data.spinHistory || [])];
  data.totalSpins = (data.totalSpins || 0) + 1;
  data.lastSpinTime = new Date().toISOString();
  
  // Actualizar puntos si el backend los envió
  if (backendResult.pointsAwarded > 0) {
    data.points = (data.points || 0) + backendResult.pointsAwarded;
  }
  
  // Guardar premio si es real
  if (backendResult.prize && backendResult.isRealPrize) {
    const reward = {
      id: Date.now().toString(),
      displayName: backendResult.prize.displayName,
      type: backendResult.prize.type,
      value: backendResult.prize.value,
      status: backendResult.prize.status || 'pendiente',
      claimed: false,
      wonAt: new Date().toISOString()
    };
    data.rewards = [reward, ...(data.rewards || [])];
    
    // Acreditar tickets automáticos
    if (backendResult.prize.type === 'free_spin' || 
        (backendResult.prize.type === 'ticket' && backendResult.prize.ticketType === 'normal')) {
      data.normalTickets = (data.normalTickets || 0) + (backendResult.prize.value || 1);
      if (!data.normalTicketsExpiry) {
        data.normalTicketsExpiry = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      }
    }
    
    if (backendResult.prize.type === 'ticket' && backendResult.prize.ticketType === 'golden') {
      data.goldenTickets = (data.goldenTickets || 0) + (backendResult.prize.value || 1);
    }
  }
  
  saveUserData(data);
  return spinRecord;
};

// Rewards
export const getRewards = () => {
  const data = getUserData();
  return data.rewards || [];
};

export const claimReward = (rewardId) => {
  const data = getUserData();
  const rewardIndex = data.rewards.findIndex(r => r.id === rewardId);
  
  if (rewardIndex !== -1) {
    const reward = data.rewards[rewardIndex];
    reward.claimed = true;
    reward.claimedAt = new Date().toISOString();
    
    if (reward.type === 'cash' || reward.type === 'jackpot') {
      reward.status = 'en_proceso';
    } else {
      reward.status = 'reclamado';
    }
    
    saveUserData(data);
    return true;
  }
  return false;
};

// Purchases
export const createPurchase = (packageData, paymentMethod) => {
  const purchase = {
    id: Date.now().toString(),
    packageId: packageData.id,
    packageName: packageData.name,
    packageType: packageData.type,
    normalTickets: packageData.normalTickets || 0,
    goldenTickets: packageData.goldenTickets || 0,
    price: packageData.price,
    paymentMethod: paymentMethod,
    status: 'pending',
    createdAt: new Date().toISOString()
  };
  
  const data = getUserData();
  data.purchases = [purchase, ...(data.purchases || [])];
  saveUserData(data);
  
  return purchase.id;
};

export const completePurchase = (purchaseId) => {
  const data = getUserData();
  const purchaseIndex = data.purchases.findIndex(p => p.id === purchaseId);
  
  if (purchaseIndex === -1) {
    return { success: false, error: 'Compra no encontrada' };
  }
  
  const purchase = data.purchases[purchaseIndex];
  purchase.status = 'completed';
  purchase.completedAt = new Date().toISOString();
  
  if (purchase.normalTickets > 0) {
    data.normalTickets = (data.normalTickets || 0) + purchase.normalTickets;
    data.normalTicketsExpiry = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  }
  
  if (purchase.goldenTickets > 0) {
    data.goldenTickets = (data.goldenTickets || 0) + purchase.goldenTickets;
  }
  
  saveUserData(data);
  return { success: true, purchase };
};

export const getPurchases = () => {
  const data = getUserData();
  return data.purchases || [];
};

// Get expiry time remaining
export const getExpiryText = () => {
  const data = getUserData();
  if (!data.normalTicketsExpiry) return null;
  
  const expiry = new Date(data.normalTicketsExpiry);
  const now = new Date();
  const diff = expiry - now;
  
  if (diff <= 0) return 'Expirados';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return `${hours}h ${minutes}m`;
};
